<script setup lang="ts">
  import { computed } from "vue";
  import Card from "@/components/atoms/Card.vue";

  export type IndicatorCardValueType = "sum" | "percent" | "money";

  export type IndicatorCardItem = {
    name: string;
    value: number | string;
    percent?: number;
    valueType?: IndicatorCardValueType;
    currency?: string;
  };

  type Props = IndicatorCardItem;

  const props = defineProps<Props>();

  const emit = defineEmits<{ click: [event: PointerEvent] }>();

  const formattedValue = computed(() => {
    if (typeof props.value === "string") return props.value;

    // At most two fraction digits (integers stay clean, min defaults to 0).
    const ruNumber = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 });

    switch (props.valueType) {
      case "money":
        return `${ruNumber.format(props.value)} ${props.currency ?? "₽"}`;

      case "percent":
        return `${ruNumber.format(props.value)} %`;

      case "sum":
      default:
        return ruNumber.format(props.value);
    }
  });

  function handleClick(event: PointerEvent) {
    emit("click", event);
  }

  const isNegative = computed(() => {
    return props.percent && Math.sign(props.percent) === -1;
  });
</script>

<template>
  <Card :class="$style.base" @click="handleClick">
    <span :class="$style.name">{{ $props.name }}</span>
    <span :class="$style.value">{{ formattedValue }}</span>
    <span v-if="$props.percent" :class="[$style.percent, isNegative && $style.percent_negative]">
      {{ (isNegative ? "▼" : "▲") + " " + Math.abs($props.percent) + " %" }}
    </span>
  </Card>
</template>

<style lang="scss" module>
  .base {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 0;

    border: none;
  }

  .name {
    color: var(--muted-foreground);

    font-size: 11px;
    font-weight: 500;

    letter-spacing: 0.05em;
  }

  .value {
    font-size: 1.125rem;
    line-height: 1.75rem;
    letter-spacing: -0.025em;

    font-weight: 600;
  }

  .percent {
    font-size: 11px;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;

    color: var(--success);

    &_negative {
      color: var(--destructive);
    }
  }
</style>
